import { MutableRefObject, useCallback, useEffect, useState } from 'react'
import { TurnstileInstance } from '@marsidev/react-turnstile'
import useStore from 'stores/utils/hooks/useStore'
import { trackInAmplitude } from 'utils/tracking/amplitude'
import { AmplitudeEvent } from 'utils/tracking/amplitude/amplitude.interface'
import {
  CaptchaTriggerSource,
  handleCaptcha as handleCaptchaDomain
} from '../api/utils/handleCaptcha'
import { Experiments, optimizelyClient } from 'CustomOptimizelyProvider'
import { CAPTCHA_CTA_PAGES } from 'utils/pages'

type UseCaptchaParams = {
  captchaRef: MutableRefObject<TurnstileInstance | null>
}

export const useCaptcha = ({ captchaRef }: UseCaptchaParams) => {
  const {
    page: { isLastDisplayedPage },
    traversal: { traversalId },
    navigation: { currentPageInSession },
    captcha: { postCaptchaToken, setCaptchaFlowStatus }
  } = useStore()

  const [captchaToken, setCaptchaToken] = useState<string>('')
  const rootStore = useStore()

  const checkIfCaptchaKillSwitchIsEnabled = (): boolean => {
    const userContext = optimizelyClient.getUserContext()
    const captchaKillSwitch = userContext?.decide(
      Experiments.CAT_3614_CAPTCHA_KILL_SWITCH
    )

    if (!!captchaKillSwitch?.enabled) {
      trackInAmplitude(AmplitudeEvent.CaptchaKillSwitchIsActive, {
        currentPage: currentPageInSession
      })

      return true
    }

    return false
  }

  const shouldAwaitCaptcha = (): boolean => {
    return isLastDisplayedPage(currentPageInSession)
  }

  const shouldHandleCaptcha = (source: CaptchaTriggerSource): boolean => {
    if (checkIfCaptchaKillSwitchIsEnabled()) {
      setCaptchaFlowStatus('aborted')
      trackInAmplitude(AmplitudeEvent.CaptchaAborted)
      return false
    }

    if (source === 'ctaClick') {
      return CAPTCHA_CTA_PAGES.includes(currentPageInSession || '')
    }

    return false
  }

  const handleCaptcha = async (captchaTriggerSource: CaptchaTriggerSource) => {
    return handleCaptchaDomain({
      rootStore,
      captchaRef,
      source: captchaTriggerSource
    })
  }

  const setToken = (token: string) => {
    setCaptchaToken(token)
  }

  const submitCaptchaToken = useCallback(
    async (token: string) => {
      trackInAmplitude(AmplitudeEvent.CaptchaPostToken, {
        currentPage: currentPageInSession
      })
      try {
        await postCaptchaToken(token, traversalId)
        trackInAmplitude(AmplitudeEvent.CaptchaPostTokenSuccess, {
          currentPage: currentPageInSession
        })
        setCaptchaFlowStatus('success')
      } catch (error) {
        trackInAmplitude(AmplitudeEvent.CaptchaPostTokenError, {
          currentPage: currentPageInSession
        })
        setCaptchaFlowStatus('error')

        return
      }

      return
    },
    [currentPageInSession, postCaptchaToken, setCaptchaFlowStatus, traversalId]
  )

  useEffect(() => {
    if (captchaToken && traversalId) {
      submitCaptchaToken(captchaToken)
      setCaptchaToken('')
    }
  }, [captchaToken, submitCaptchaToken, traversalId])

  return {
    handleCaptcha,
    submitCaptchaToken,
    shouldAwaitCaptcha,
    shouldHandleCaptcha,
    setToken
  }
}
